import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { FIREBASE_CONFIG } from '@viralarc/constants';
import { initializeApp } from 'firebase/app';
import './App.css';

// Initialize Firebase
initializeApp(FIREBASE_CONFIG);

// Landing Pages
import LandingPage from './pages/landing/LandingPage';
import LandingHomePage from './pages/landing/HomePage';
import FeaturesPage from './pages/landing/FeaturesPage';
import PricingPage from './pages/landing/PricingPage';
import CompanyPage from './pages/landing/CompanyPage';
import PrivacyPolicyPage from './pages/landing/PrivacyPolicyPage';
import TermsOfServicePage from './pages/landing/TermsOfServicePage';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';

// Main Pages
import HomePage from './pages/HomePage';
import SocialAccountPage from './pages/SocialAccountPage';
import PostsPage from './pages/home-publish/PostsPage';
import ProjectsPage from './pages/ProjectsPage';
import TrashPage from './pages/TrashPage';
import LibraryPage from './pages/LibraryPage';
import PostsCommentsPage from './pages/home-comments/PostsCommentsPage';
import TopicTrackingPage from './pages/TopicTrackingPage';
import AutoManagementProjectPage from './pages/home-auto/AutoManagementProjectPage';
import CheckoutPage from './pages/CheckoutPage';
import PaymentSuccessPage from './pages/payment/PaymentSuccessPage';
import AIWritingSelect from './pages/home-text/ai-writing-select';
import AutomationHub from './pages/home-auto/AutomationHub';
import DashboardInsightsPage from './pages/home-insights/DashboardInsightsPage';
import MyAutomationsPage from './pages/MyAutomationsPage';

// Template Pages
import ContentGeneratorSelect from './pages/home-carousel/content-generator-select';
import ContentGeneratorIdea from './pages/home-carousel/content-generator-idea';
import ContentGeneratorGoogle from './pages/home-carousel/content-generator-google';
import ContentGeneratorThreads from './pages/home-carousel/content-generator-threads';
import ContentGeneratorYoutube from './pages/home-carousel/content-generator-youtube';
import ContentGeneratorStep2 from './pages/home-carousel/content-generator-step2';
import ContentGeneratorStep3 from './pages/home-carousel/content-generator-step3';
import ContentGeneratorStep4 from './pages/home-carousel/content-generator-step4';
import AIWritingTool from './pages/home-text/ai-writing-tool';
import AIWritingIdea from './pages/home-text/ai-writing-idea';
import CreatorToScriptPage from './pages/home-video/creator-to-script';
import CreatorToScriptSelect from './pages/home-video/creator-to-script-select';
import SubtitleGenerator from './pages/home-video/subtitle-generator';
import AIWritingRandom from './pages/home-text/ai-writing-random';

// Settings Pages
import CreditsPage from './pages/settings/CreditsPage';
import UserInfoPage from './pages/settings/UserInfoPage';
import SettingsPage from './pages/settings/SettingsPage';

// Tool Pages
import FakeTextGenerator from './pages/home-toolbox/FakeTextGenerator';
import FakeNotificationGenerator from './pages/home-toolbox/FakeNotificationGenerator';
import FakeCallGenerator from './pages/home-toolbox/FakeCallGenerator';

// Onboarding Pages
import Step1ConnectAccount from './pages/onboarding/Step1ConnectAccount';
import Step2ContentPurpose from './pages/onboarding/Step2ContentPurpose';
import Step3ContentFormat from './pages/onboarding/Step3ContentFormat';
import Step4ThemeFit from './pages/onboarding/Step4ThemeFit';
import Step5ReferenceAccounts from './pages/onboarding/Step5ReferenceAccounts';
import Step6DecisionScope from './pages/onboarding/Step6DecisionScope';
import PrivateRoute from './utils/PrivateRoute';
import AccountRequiredRoute from './utils/AccountRequiredRoute';
import OnboardingRoute from './utils/OnboardingRoute';
import AppProviders from './contexts/AppProviders';

const routes = [
  // Landing Pages - 公開頁面
  { path: "/", element: <LandingHomePage />, private: false },
  { path: "/features", element: <FeaturesPage />, private: false },
  { path: "/pricing", element: <PricingPage />, private: false },
  { path: "/company", element: <CompanyPage />, private: false },
  { path: "/landing", element: <LandingPage />, private: false },

  // Auth Pages - 公開路由
  { path: "/login", element: <Login />, private: false },
  { path: "/register", element: <Register />, private: false },
  { path: "/forgot-password", element: <ForgotPassword />, private: false },
  { path: "/privacy-policy", element: <PrivacyPolicyPage />, private: false },
  { path: "/terms-of-service", element: <TermsOfServicePage />, private: false },

  // 引導流程頁面 - 6 步驟 onboarding (帶步驟守衛)
  {
    path: "/onboarding/step1",
    element: <OnboardingRoute step={1}><Step1ConnectAccount /></OnboardingRoute>,
    private: true,
    onboarding: true
  },
  {
    path: "/onboarding/step2",
    element: <OnboardingRoute step={2}><Step2ContentPurpose /></OnboardingRoute>,
    private: true,
    onboarding: true
  },
  {
    path: "/onboarding/step3",
    element: <OnboardingRoute step={3}><Step3ContentFormat /></OnboardingRoute>,
    private: true,
    onboarding: true
  },
  {
    path: "/onboarding/step4",
    element: <OnboardingRoute step={4}><Step4ThemeFit /></OnboardingRoute>,
    private: true,
    onboarding: true
  },
  {
    path: "/onboarding/step5",
    element: <OnboardingRoute step={5}><Step5ReferenceAccounts /></OnboardingRoute>,
    private: true,
    onboarding: true
  },
  {
    path: "/onboarding/step6",
    element: <OnboardingRoute step={6}><Step6DecisionScope /></OnboardingRoute>,
    private: true,
    onboarding: true
  },
  // 舊的 /welcome 路徑重定向到 onboarding
  { path: "/welcome", element: <Navigate to="/onboarding/step1" replace />, private: true },

  // Payment
  { path: "/checkout", element: <CheckoutPage />, private: true },
  { path: "/payment-success", element: <PaymentSuccessPage />, private: true },

  // Main Pages
  { path: "/dashboard", element: <Navigate to="/home" replace />, private: false },
  { path: "/projects", element: <ProjectsPage />, private: true },
  { path: "/home", element: <HomePage />, private: true },
  { path: "/trash", element: <TrashPage />, private: true },
  { path: "/library", element: <LibraryPage />, private: true },
  { path: "/posts-and-comments", element: <PostsCommentsPage />, private: true },
  { path: "/topic-tracking", element: <TopicTrackingPage />, private: true },
  { path: "/my-automations", element: <MyAutomationsPage />, private: true },
  { path: "/accounts", element: <SocialAccountPage />, private: true },
  { path: "/dashboard-insights", element: <DashboardInsightsPage />, private: true },

  // Special Handling: AccountRequiredRoute
  {
    path: "/posts",
    element: (
      <AccountRequiredRoute>
        <PostsPage />
      </AccountRequiredRoute>
    ),
    private: false // Handled by wrapper
  },

  { path: "/ai-writing", element: <AIWritingSelect />, private: true },
  { path: "/automation", element: <AutomationHub />, private: true },

  // Unified Content Generator
  { path: "/templates/content-generator", element: <ContentGeneratorSelect />, private: true },
  { path: "/templates/content-generator/idea", element: <ContentGeneratorIdea />, private: true },
  { path: "/templates/content-generator/google", element: <ContentGeneratorGoogle />, private: true },
  { path: "/templates/content-generator/threads", element: <ContentGeneratorThreads />, private: true },
  { path: "/templates/content-generator/youtube", element: <ContentGeneratorYoutube />, private: true },
  { path: "/templates/content-generator/step2", element: <ContentGeneratorStep2 />, private: true },
  { path: "/templates/content-generator/step3", element: <ContentGeneratorStep3 />, private: true },
  { path: "/templates/content-generator/step4", element: <ContentGeneratorStep4 />, private: true },

  // Redirect old template routes
  { path: "/templates/youtube-to-post", element: <Navigate to="/templates/content-generator" replace />, private: false },

  // AI Writing Templates
  { path: "/templates/ai-tool-recommendation", element: <AIWritingTool />, private: true },
  { path: "/templates/idea-to-post", element: <AIWritingIdea />, private: true },
  { path: "/templates/creator-to-script", element: <CreatorToScriptSelect />, private: true },
  { path: "/templates/creator-to-script/ai-edit", element: <CreatorToScriptPage />, private: true },
  { path: "/templates/subtitle-generator", element: <SubtitleGenerator />, private: true },
  { path: "/templates/random-post", element: <AIWritingRandom />, private: true },

  // Profile Menu Pages
  { path: "/credits", element: <CreditsPage />, private: true },
  { path: "/account", element: <UserInfoPage />, private: true },
  { path: "/settings", element: <SettingsPage />, private: true },

  // Tools
  { path: "/tools/fake-text-generator", element: <FakeTextGenerator />, private: true },
  { path: "/tools/fake-notification-generator", element: <FakeNotificationGenerator />, private: true },
  { path: "/tools/fake-call-generator", element: <FakeCallGenerator />, private: true },

  // Auto Management Project Detail Page
  { path: "/project/:projectId", element: <AutoManagementProjectPage />, private: true },
];

const getRouteElement = (route) => {
  if (route.private) {
    return <PrivateRoute>{route.element}</PrivateRoute>;
  }
  return route.element;
};

function AppContent() {
  const location = useLocation();

  return (
    <div className="App">
      <main className="app-main">
        <Routes>
          {routes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={getRouteElement(route)}
            />
          ))}
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <AppProviders>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
      </Router>
    </AppProviders>
  );
}

export default App;